// Single source of truth for source BIAS + RELIABILITY visual primitives.
//
// Two dimensions, two perceptual channels — never shared:
//   bias        -> hue (+ horizontal position on the SpectrumStrip):
//                  blue = left, red = right, gray = center
//   reliability -> fill WEIGHT of the same dot (solid -> hollow), never a hue.
//                  Hue is reserved for lean, so trust can't paint a dot red and
//                  collide with "right-leaning".
//
// The ±6 wing cutoff and the 24/32/40 reliability tiers match the pipeline
// (bias_utils, min_reliability_for_facts = 24) and the story SpectrumStrip.

export const BIAS_WING = 6

// Reliability tiers (outlet score, ~0-64). 24 is the pipeline's facts floor:
// below it an outlet never contributes to facts, and renders as a hollow dot.
export const RELIABILITY = { reliable: 40, mostly: 32, mixed: 24 }

export function reliabilityTier(reliability) {
  const v = reliability ?? 0
  if (v >= RELIABILITY.reliable) return 'reliable'
  if (v >= RELIABILITY.mostly) return 'mostly'
  if (v >= RELIABILITY.mixed) return 'mixed'
  return 'low'
}

// Legacy 4-dot meter count (a few panels still render dots; converging them
// onto the single mark is the follow-up sweep).
export function reliabilityDots(reliability) {
  return { reliable: 4, mostly: 3, mixed: 2, low: 1 }[reliabilityTier(reliability)]
}

// Fill weight for the one-mark dot. solid = trustworthy, hollow = below the
// facts floor, so shaky sources recede without a second color.
//   reliable / mostly -> 'solid'
//   mixed             -> 'faded'
//   low               -> 'hollow'
export function reliabilityFill(reliability) {
  const t = reliabilityTier(reliability)
  if (t === 'low') return 'hollow'
  if (t === 'mixed') return 'faded'
  return 'solid'
}

// Bias dot hue ramp. Full literal Tailwind classes (bg + matching border) so
// the JIT compiler keeps them and the hollow-ring variant has a border color.
// Magnitude is carried by position (strips) or the text label (cards), but the
// card dot keeps a 7-stop ramp so "Far Left" reads darker than "Leans Left".
export function biasRamp(bias) {
  const b = bias ?? 0
  if (b < -18) return { bg: 'bg-blue-700', border: 'border-blue-700' }
  if (b < -12) return { bg: 'bg-blue-600', border: 'border-blue-600' }
  if (b < -BIAS_WING) return { bg: 'bg-blue-400', border: 'border-blue-400' }
  if (b < BIAS_WING) return { bg: 'bg-zinc-400', border: 'border-zinc-400' }
  if (b < 12) return { bg: 'bg-red-400', border: 'border-red-400' }
  if (b < 18) return { bg: 'bg-red-600', border: 'border-red-600' }
  return { bg: 'bg-red-700', border: 'border-red-700' }
}

export function biasBgClass(bias) {
  return biasRamp(bias).bg
}
